import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/Layout';

// Lazy-loaded pages for code splitting
// INICIO
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Shift = lazy(() => import('./pages/Shift').then(m => ({ default: m.Shift })));
const QuickView = lazy(() => import('./pages/QuickView').then(m => ({ default: m.QuickView })));

// CONFIGURACION
const Industries = lazy(() => import('./pages/Industries').then(m => ({ default: m.Industries })));
const Materials = lazy(() => import('./pages/Materials').then(m => ({ default: m.Materials })));
const Presets = lazy(() => import('./pages/Presets').then(m => ({ default: m.Presets })));
const Sites = lazy(() => import('./pages/Sites').then(m => ({ default: m.Sites })));
const DataIngestion = lazy(() => import('./pages/DataIngestion').then(m => ({ default: m.DataIngestion })));
const InverterInventory = lazy(() => import('./pages/InverterInventory').then(m => ({ default: m.InverterInventory })));

// ENTRENAMIENTO
const Training = lazy(() => import('./pages/Training').then(m => ({ default: m.Training })));
const EarlyWarning = lazy(() => import('./pages/EarlyWarning').then(m => ({ default: m.EarlyWarning })));
const PredictionHub = lazy(() => import('./pages/PredictionHub').then(m => ({ default: m.PredictionHub })));
const DcStringHealth = lazy(() => import('./pages/DcStringHealth').then(m => ({ default: m.DcStringHealth })));
const PeckEnvironment = lazy(() => import('./pages/PeckEnvironment').then(m => ({ default: m.PeckEnvironment })));
const SignalHealth = lazy(() => import('./pages/SignalHealth').then(m => ({ default: m.SignalHealth })));
const DataSources = lazy(() => import('./pages/DataSources').then(m => ({ default: m.DataSources })));
const ModelRegistry = lazy(() => import('./pages/ModelRegistry').then(m => ({ default: m.ModelRegistry })));
const Validation = lazy(() => import('./pages/Validation').then(m => ({ default: m.Validation })));
const PhysicsEquations = lazy(() => import('./pages/PhysicsEquations').then(m => ({ default: m.PhysicsEquations })));
const ComponentFeatures = lazy(() => import('./pages/ComponentFeatures').then(m => ({ default: m.ComponentFeatures })));
const ModelHistory = lazy(() => import('./pages/ModelHistory').then(m => ({ default: m.ModelHistory })));

// PREDICCION
const Prediction = lazy(() => import('./pages/Prediction').then(m => ({ default: m.Prediction })));
const PredictionRul = lazy(() => import('./pages/PredictionRul').then(m => ({ default: m.PredictionRul })));
const TemporalPrediction = lazy(() => import('./pages/TemporalPrediction').then(m => ({ default: m.TemporalPrediction })));
const BatchPrediction = lazy(() => import('./pages/BatchPrediction').then(m => ({ default: m.BatchPrediction })));
const Analysis = lazy(() => import('./pages/Analysis').then(m => ({ default: m.Analysis })));
const Scenarios = lazy(() => import('./pages/Scenarios').then(m => ({ default: m.Scenarios })));
const ThermalAnalysis = lazy(() => import('./pages/ThermalAnalysis').then(m => ({ default: m.ThermalAnalysis })));
const DigitalTwin = lazy(() => import('./pages/DigitalTwin').then(m => ({ default: m.DigitalTwin })));
const WaveformAnalysis = lazy(() => import('./pages/WaveformAnalysis').then(m => ({ default: m.WaveformAnalysis })));
const RcaSimulator = lazy(() => import('./pages/RcaSimulator').then(m => ({ default: m.RcaSimulator })));
const FleetHealth = lazy(() => import('./pages/FleetHealth').then(m => ({ default: m.FleetHealth })));
const DataArchitecture = lazy(() => import('./pages/DataArchitecture').then(m => ({ default: m.DataArchitecture })));
const DataQuality = lazy(() => import('./pages/DataQuality').then(m => ({ default: m.DataQuality })));
const PINNDashboard = lazy(() => import('./pages/PINNDashboard').then(m => ({ default: m.PINNDashboard })));
const PredictiveIndicators = lazy(() => import('./pages/PredictiveIndicators').then(m => ({ default: m.PredictiveIndicators })));

// MONITORIZACION
const Monitoring = lazy(() => import('./pages/Monitoring').then(m => ({ default: m.Monitoring })));
const Alerts = lazy(() => import('./pages/Alerts').then(m => ({ default: m.Alerts })));
const Trends = lazy(() => import('./pages/Trends').then(m => ({ default: m.Trends })));
// PlantMap removed — Sites page covers this functionality
const Logs = lazy(() => import('./pages/Logs').then(m => ({ default: m.Logs })));

// AUDITORIA
const History = lazy(() => import('./pages/History').then(m => ({ default: m.History })));
const Reports = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const Statistics = lazy(() => import('./pages/Statistics').then(m => ({ default: m.Statistics })));
const Traceability = lazy(() => import('./pages/Traceability').then(m => ({ default: m.Traceability })));
const Compliance = lazy(() => import('./pages/Compliance').then(m => ({ default: m.Compliance })));

// AUTH
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));

// ASSISTANT
const ChatHistory = lazy(() => import('./pages/ChatHistory').then(m => ({ default: m.ChatHistory })));

// ADMIN
const AdminHealth = lazy(() => import('./pages/AdminHealth').then(m => ({ default: m.AdminHealth })));
const UseCaseHub = lazy(() => import('./pages/UseCaseHub').then(m => ({ default: m.UseCaseHub })));
const InverterDetail = lazy(() => import('./pages/InverterDetail').then(m => ({ default: m.InverterDetail })));
const FailureReport = lazy(() => import('./pages/FailureReport').then(m => ({ default: m.FailureReport })));
const MaintenancePlanner = lazy(() => import('./pages/MaintenancePlanner').then(m => ({ default: m.MaintenancePlanner })));
const ProjectStatus = lazy(() => import('./pages/ProjectStatus').then(m => ({ default: m.ProjectStatus })));
const DemoGuide = lazy(() => import('./pages/DemoGuide').then(m => ({ default: m.DemoGuide })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* INICIO */}
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/shift" element={<Shift />} />
                      <Route path="/quick" element={<QuickView />} />

                      {/* CONFIGURACION */}
                      <Route path="/types" element={<Industries />} />
                      <Route path="/components" element={<Materials />} />
                      <Route path="/presets" element={<Presets />} />
                      <Route path="/sites" element={<Sites />} />
                      <Route path="/data-ingestion" element={<DataIngestion />} />
                      <Route path="/inverter-inventory" element={<InverterInventory />} />

                      {/* ENTRENAMIENTO */}
                      <Route path="/training" element={<Training />} />
                      <Route path="/data-sources" element={<DataSources />} />
                      <Route path="/model-registry" element={<ModelRegistry />} />
                      <Route path="/validation" element={<Validation />} />
                      <Route path="/physics" element={<PhysicsEquations />} />
                      <Route path="/component-features" element={<ComponentFeatures />} />
                      <Route path="/model-history" element={<ModelHistory />} />

                      {/* PREDICCION */}
                      <Route path="/predict" element={<Prediction />} />
                      <Route path="/prediction" element={<PredictionHub />} />
                      <Route path="/prediction/short" element={<EarlyWarning />} />
                      <Route path="/prediction/dc" element={<DcStringHealth />} />
                      <Route path="/prediction/peck" element={<PeckEnvironment />} />
                      <Route path="/prediction/fan" element={<SignalHealth signal="fan" />} />
                      <Route path="/prediction/thermal" element={<SignalHealth signal="thermal" />} />
                      <Route path="/prediction/rul" element={<PredictionRul />} />
                      <Route path="/temporal" element={<TemporalPrediction />} />
                      <Route path="/batch" element={<BatchPrediction />} />
                      <Route path="/analysis" element={<Analysis />} />
                      <Route path="/scenarios" element={<Scenarios />} />
                      <Route path="/thermal" element={<ThermalAnalysis />} />
                      <Route path="/indicators" element={<PredictiveIndicators />} />
                      <Route path="/digital-twin" element={<DigitalTwin />} />

                      {/* MONITORIZACION */}
                      <Route path="/monitoring" element={<Monitoring />} />
                      <Route path="/alerts" element={<Alerts />} />
                      <Route path="/trends" element={<Trends />} />
                      {/* plant-map removed — Sites covers this */}
                      <Route path="/logs" element={<Logs />} />

                      {/* AUDITORIA */}
                      <Route path="/history" element={<History />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/statistics" element={<Statistics />} />
                      <Route path="/traceability" element={<Traceability />} />
                      <Route path="/compliance" element={<Compliance />} />

                      {/* ADMIN */}
                      <Route path="/admin/health" element={<AdminHealth />} />
                      <Route path="/use-cases" element={<UseCaseHub />} />
                      <Route path="/waveform-analysis" element={<WaveformAnalysis />} />
                      <Route path="/rca-simulator" element={<RcaSimulator />} />
                      <Route path="/fleet-health" element={<FleetHealth />} />
                      <Route path="/data-architecture" element={<DataArchitecture />} />
                      <Route path="/data-quality" element={<DataQuality />} />
                      <Route path="/pinn" element={<PINNDashboard />} />
                      <Route path="/inverter/:id" element={<InverterDetail />} />
                      <Route path="/failure/:id" element={<FailureReport />} />
                      <Route path="/maintenance-planner" element={<MaintenancePlanner />} />
                      <Route path="/project-status" element={<ProjectStatus />} />
                      <Route path="/demo-guide" element={<DemoGuide />} />
                      <Route path="/chat-history" element={<ChatHistory />} />
                    </Routes>
                  </Suspense>
                </Layout>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
